import React from 'react';
import { Layout, Menu, Navigation, List, Link as LinkIcon } from 'lucide-react';

interface HeaderEditorProps {
  settings: any;
  content: any;
  onSettingsChange: (settings: any) => void;
  onContentChange: (content: any) => void;
}

const HeaderEditor: React.FC<HeaderEditorProps> = ({ 
  settings, 
  content, 
  onSettingsChange, 
  onContentChange 
}) => {
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : type === 'number' 
        ? parseInt(value) 
        : value;
    
    onSettingsChange({ [name]: newValue });
  };
  
  const handleMenuItemChange = (index: number, field: string, value: string) => {
    const newMenuItems = [...(content?.menuItems || [])];
    newMenuItems[index] = { ...newMenuItems[index], [field]: value };
    onContentChange({ menuItems: newMenuItems });
  };
  
  const handleAddMenuItem = () => {
    const newMenuItems = [...(content?.menuItems || [])];
    newMenuItems.push({ label: '', url: '', isDropdown: false, children: [] });
    onContentChange({ menuItems: newMenuItems });
  };
  
  const handleRemoveMenuItem = (index: number) => {
    const newMenuItems = [...(content?.menuItems || [])];
    newMenuItems.splice(index, 1);
    onContentChange({ menuItems: newMenuItems });
  };
  
  const handleAddSubMenuItem = (parentIndex: number) => {
    const newMenuItems = [...(content?.menuItems || [])];
    if (!newMenuItems[parentIndex].children) {
      newMenuItems[parentIndex].children = [];
    }
    newMenuItems[parentIndex].children.push({ label: '', url: '' });
    newMenuItems[parentIndex].isDropdown = true;
    onContentChange({ menuItems: newMenuItems });
  };
  
  const handleSubMenuItemChange = (parentIndex: number, childIndex: number, field: string, value: string) => {
    const newMenuItems = [...(content?.menuItems || [])];
    newMenuItems[parentIndex].children[childIndex] = { 
      ...newMenuItems[parentIndex].children[childIndex], 
      [field]: value 
    };
    onContentChange({ menuItems: newMenuItems });
  };
  
  const handleRemoveSubMenuItem = (parentIndex: number, childIndex: number) => {
    const newMenuItems = [...(content?.menuItems || [])];
    newMenuItems[parentIndex].children.splice(childIndex, 1);
    if (newMenuItems[parentIndex].children.length === 0) {
      newMenuItems[parentIndex].isDropdown = false;
    }
    onContentChange({ menuItems: newMenuItems });
  };
  
  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Genel Ayarlar</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Logo Metni
            </label>
            <input
              type="text"
              name="logoText"
              value={settings?.logoText || 'KREDİPARK'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Logo URL (Opsiyonel)
            </label>
            <input
              type="text"
              name="logoUrl"
              value={settings?.logoUrl || ''}
              onChange={handleSettingChange}
              placeholder="https://example.com/logo.png"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Arka Plan Rengi
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                name="backgroundColor"
                value={settings?.backgroundColor || '#F7BA45'}
                onChange={handleSettingChange}
                className="w-10 h-10 p-1 border border-slate-200 rounded-lg"
              />
              <input
                type="text"
                name="backgroundColor"
                value={settings?.backgroundColor || '#F7BA45'}
                onChange={handleSettingChange}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Metin Rengi
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                name="textColor"
                value={settings?.textColor || '#1e293b'}
                onChange={handleSettingChange}
                className="w-10 h-10 p-1 border border-slate-200 rounded-lg"
              />
              <input
                type="text"
                name="textColor"
                value={settings?.textColor || '#1e293b'}
                onChange={handleSettingChange}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="isSticky"
              checked={settings?.isSticky !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">Sabit Header (Sticky)</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="showLoginButton"
              checked={settings?.showLoginButton !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">Giriş Butonunu Göster</span>
          </label>
        </div>
      </div>
      
      {/* Menu Items */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-slate-700">Menü Öğeleri</h3>
          <button
            onClick={handleAddMenuItem}
            className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
          >
            Menü Öğesi Ekle
          </button>
        </div>
        
        <div className="space-y-4">
          {(content?.menuItems || []).map((item: any, index: number) => (
            <div key={index} className="border border-slate-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-slate-700">Menü Öğesi {index + 1}</h4>
                <button
                  onClick={() => handleRemoveMenuItem(index)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18"></path>
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-xs text-slate-500">
                    Etiket
                  </label>
                  <input
                    type="text"
                    value={item.label || ''}
                    onChange={(e) => handleMenuItemChange(index, 'label', e.target.value)}
                    placeholder="Menü Öğesi"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-xs text-slate-500">
                    URL
                  </label>
                  <input
                    type="text"
                    value={item.url || ''}
                    onChange={(e) => handleMenuItemChange(index, 'url', e.target.value)}
                    placeholder="/sayfa-url"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.isDropdown || false}
                    onChange={(e) => handleMenuItemChange(index, 'isDropdown', e.target.checked ? 'true' : 'false')}
                    className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                  />
                  <span className="text-sm text-slate-600">Açılır Menü</span>
                </label>
                
                {item.isDropdown && (
                  <button
                    onClick={() => handleAddSubMenuItem(index)}
                    className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
                  >
                    Alt Menü Ekle
                  </button>
                )}
              </div>
              
              {item.isDropdown && item.children && item.children.length > 0 && (
                <div className="pl-4 border-l-2 border-slate-200 space-y-3">
                  {item.children.map((child: any, childIndex: number) => (
                    <div key={childIndex} className="flex items-center gap-2">
                      <div className="grid grid-cols-2 gap-2 flex-1">
                        <input
                          type="text"
                          value={child.label || ''}
                          onChange={(e) => handleSubMenuItemChange(index, childIndex, 'label', e.target.value)}
                          placeholder="Alt Menü Etiketi"
                          className="px-2 py-1 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        />
                        <input
                          type="text"
                          value={child.url || ''}
                          onChange={(e) => handleSubMenuItemChange(index, childIndex, 'url', e.target.value)}
                          placeholder="/alt-sayfa-url"
                          className="px-2 py-1 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveSubMenuItem(index, childIndex)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 6L6 18"></path>
                          <path d="M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {(content?.menuItems || []).length === 0 && (
            <div className="text-center p-6 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              <p className="text-slate-500 mb-2">Henüz menü öğesi eklenmedi</p>
              <button
                onClick={handleAddMenuItem}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"></path>
                </svg>
                <span>İlk Menü Öğesini Ekle</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeaderEditor;